import { useIncidentDetail } from 'core/components/JianDetail/hooks/useIncidentDetail';
import {
  BaseInfoOccupationEnum,
  BaseInfoSexEnum,
  ECGWaveformEnum,
  EnteredUserQualificationEnum,
  FindEmsContactBleedingValueEnum,
  FindEmsContactIncontinenceEnum,
  IncidentTypeEnum,
  IntubationDevice,
  JCSEnum,
  MeasureTimingEnum,
  MedicationStatusTypeEnum,
  PupilReactionEnum,
  RequestSummaryADLEnum,
  RequestSummaryMedicalHistoryEnum,
  RequestSummaryOnsetTypeEnum,
  TreatmentCPADataTimingEnum,
  TreatmentInitialEcgEnum,
  TreatmentIvAccessTimingEnum,
  VitalBtPartEnum,
  VitalO2MeanEnum,
  VitalPupilPositionEnum,
} from 'core/gen/schemas';

/////////////////////////////////////////////
//
// 事案
//
/////////////////////////////////////////////
/**
 * 事案種別の日本語ラベルマッピング
 */
const INCIDENT_TYPE_LABELS = {
  [IncidentTypeEnum.SUDDEN_ILLNESS]: '急病',
  [IncidentTypeEnum.GENERAL_INJURY]: '一般負傷',
  [IncidentTypeEnum.HOSPITAL_TRANSFER]: '転院搬送',
  [IncidentTypeEnum.TRAFFIC]: '交通',
  [IncidentTypeEnum.FIRE]: '火災',
  [IncidentTypeEnum.NATURAL_DISASTER]: '自然災害',
  [IncidentTypeEnum.DROWNING]: '水難',
  [IncidentTypeEnum.WORK_ACCIDENT]: '労働災害',
  [IncidentTypeEnum.SPORTS]: '運動競技',
  [IncidentTypeEnum.ASSAULT]: '加害',
  [IncidentTypeEnum.SELF_INJURY]: '自損行為',
  [IncidentTypeEnum.DOCTOR_TRANSPORT]: '医師搬送',
  [IncidentTypeEnum.EQUIPMENT_TRANSPORT]: '資機材輸送',
  [IncidentTypeEnum.OTHER]: 'その他',
  // 以下は広島では利用しない
  [IncidentTypeEnum.HOSPITAL_TRANSFER_SUPPORT]: '',
} as const satisfies Record<keyof typeof IncidentTypeEnum, string>;

/**
 * 事案種別の日本語ラベルを取得する関数
 *
 * @param incidentType - 事案種別
 * @returns 日本語ラベル
 */
const getIncidentTypeLabel = (
  incidentType: keyof typeof IncidentTypeEnum | null | undefined
): string => {
  if (!incidentType || !(incidentType in INCIDENT_TYPE_LABELS)) {
    return '';
  }

  return INCIDENT_TYPE_LABELS[incidentType];
};

/**
 * 事案記入者の保持資格名の日本語ラベルマッピング
 */
const INCIDENT_ENTERED_USER_QUALIFICATION_LABELS = {
  [EnteredUserQualificationEnum.PARAMEDIC_LICENSE]: '救命士',
  [EnteredUserQualificationEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof EnteredUserQualificationEnum, string>;

/**
 * 事案記入者の保持資格名の日本語ラベルを取得する関数
 *
 * @param enteredUserQualification - 事案記入者の保持資格名
 * @returns 日本語ラベル
 */
const getEnteredUserQualificationLabel = (
  enteredUserQualification:
    | keyof typeof EnteredUserQualificationEnum
    | null
    | undefined
): string => {
  if (
    !enteredUserQualification ||
    !(enteredUserQualification in INCIDENT_ENTERED_USER_QUALIFICATION_LABELS)
  ) {
    return '';
  }
  return INCIDENT_ENTERED_USER_QUALIFICATION_LABELS[enteredUserQualification];
};

/////////////////////////////////////////////
//
// 傷病者 → 基本情報（BaseInfo）
//
/////////////////////////////////////////////
/**
 * 傷病者の性別の日本語ラベルマッピング
 */
const PATIENT_SEX_LABELS = {
  [BaseInfoSexEnum.MALE]: '男性',
  [BaseInfoSexEnum.FEMALE]: '女性',
  [BaseInfoSexEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof BaseInfoSexEnum, string>;

/**
 * 傷病者の性別の日本語ラベルを取得する関数
 *
 * @param patientSex - 傷病者の性別
 * @returns 日本語ラベル
 */
const getPatientSexLabel = (
  patientSex: keyof typeof BaseInfoSexEnum | null | undefined
): string => {
  if (!patientSex || !(patientSex in PATIENT_SEX_LABELS)) {
    return '';
  }

  return PATIENT_SEX_LABELS[patientSex];
};

/**
 * 職業の日本語ラベルマッピング
 */
const OCCUPATION_LABELS = {
  [BaseInfoOccupationEnum.EMPLOYEE]: '会社員',
  [BaseInfoOccupationEnum.GOVERNMENT]: '公務員',
  [BaseInfoOccupationEnum.SELF]: '自営業',
  [BaseInfoOccupationEnum.MEDICAL]: '医療関係',
  [BaseInfoOccupationEnum.SERVICE_WORKER]: 'サービス業',
  [BaseInfoOccupationEnum.FREELANCER]: '自由業（弁護士・会計士・芸術家等）',
  [BaseInfoOccupationEnum.STUDENT]: '学生',
  [BaseInfoOccupationEnum.HOMEMAKER]: '主婦（主夫）',
  [BaseInfoOccupationEnum.PART_TIME_WORKER]: 'パート・アルバイト',
  [BaseInfoOccupationEnum.UNEMPLOYED]: '無職',
  [BaseInfoOccupationEnum.OTHER]: 'その他',
  [BaseInfoOccupationEnum.UNKNOWN]: '不明',
  // 以下は広島では利用しない
  [BaseInfoOccupationEnum.HOUSEWIFE]: '',
  [BaseInfoOccupationEnum.CHILD]: '',
  [BaseInfoOccupationEnum.INFANT]: '',
  [BaseInfoOccupationEnum.NONPROFIT]: '',
  [BaseInfoOccupationEnum.ELEMENTARY_SCHOOL_STUDENT]: '',
  [BaseInfoOccupationEnum.MIDDLE_SCHOOL_STUDENT]: '',
  [BaseInfoOccupationEnum.HIGH_SCHOOL_STUDENT]: '',
  [BaseInfoOccupationEnum.TECHNICAL_COLLEGE_STUDENT]: '',
  [BaseInfoOccupationEnum.JUNIOR_COLLEGE_STUDENT]: '',
  [BaseInfoOccupationEnum.UNIVERSITY_STUDENT]: '',
  [BaseInfoOccupationEnum.VOCATIONAL_SCHOOL_STUDENT]: '',
  [BaseInfoOccupationEnum.MAFF]: '',
  [BaseInfoOccupationEnum.LABORER]: '',
} as const satisfies Record<keyof typeof BaseInfoOccupationEnum, string>;

/**
 * 職業の日本語ラベルを取得する関数
 *
 * @param occupation - 職業
 * @returns 日本語ラベル
 */
const getOccupationLabel = (
  occupation: keyof typeof BaseInfoOccupationEnum | null | undefined
): string => {
  if (!occupation || !(occupation in OCCUPATION_LABELS)) {
    return '';
  }
  return OCCUPATION_LABELS[occupation];
};

/////////////////////////////////////////////
//
// 傷病者 → 要請概要欄（RequestSummary）
//
/////////////////////////////////////////////
/**
 * 傷病者の服薬種類の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_MEDICATION_STATUS_TYPE_LABELS = {
  [MedicationStatusTypeEnum.PRESCRIPTION]: '処方薬',
  [MedicationStatusTypeEnum.OTC]: '市販薬',
  // 以下は広島では利用しない
  [MedicationStatusTypeEnum.UNKNOWN]: '',
} as const satisfies Record<keyof typeof MedicationStatusTypeEnum, string>;

const getRequestSummaryMedicationStatusTypeLabel = (
  medicationStatusType: keyof typeof MedicationStatusTypeEnum | null | undefined
): string => {
  if (
    !medicationStatusType ||
    !(medicationStatusType in REQUEST_SUMMARY_MEDICATION_STATUS_TYPE_LABELS)
  ) {
    return '';
  }
  return REQUEST_SUMMARY_MEDICATION_STATUS_TYPE_LABELS[medicationStatusType];
};

/**
 * 傷病者の発症タイプの日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ONSET_TYPE_LABELS = {
  [RequestSummaryOnsetTypeEnum.ESTIMATED]: '推定',
  [RequestSummaryOnsetTypeEnum.CONFIRMED]: '確認',
  // 以下は広島では利用しない
  [RequestSummaryOnsetTypeEnum.UNKNOWN]: '',
} as const satisfies Record<keyof typeof RequestSummaryOnsetTypeEnum, string>;

/**
 * 傷病者の発症タイプの日本語ラベルを取得する関数
 *
 * @param onsetType - 傷病者の発症タイプ
 * @returns 日本語ラベル
 */
const getRequestSummaryOnsetTypeLabel = (
  onsetType: keyof typeof RequestSummaryOnsetTypeEnum | null | undefined
): string => {
  if (!onsetType || !(onsetType in REQUEST_SUMMARY_ONSET_TYPE_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ONSET_TYPE_LABELS[onsetType];
};

/**
 * 傷病者の既往歴の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS = {
  [RequestSummaryMedicalHistoryEnum.HYPER_TENSION]: '高血圧',
  [RequestSummaryMedicalHistoryEnum.DIABETES]: '糖尿病',
  [RequestSummaryMedicalHistoryEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
  [RequestSummaryMedicalHistoryEnum.ANGINA]: '狭心症',
  [RequestSummaryMedicalHistoryEnum.HEART_FAILURE]: '心不全',
  [RequestSummaryMedicalHistoryEnum.HYPER_LIPIDEMIA]: '高脂血症',
  [RequestSummaryMedicalHistoryEnum.CANCER]: '癌',
  [RequestSummaryMedicalHistoryEnum.ASTHMA]: '喘息',
  [RequestSummaryMedicalHistoryEnum.COPD]: 'COPD',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_INFARCTION]: '脳梗塞',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_HEMORRHAGE]: '脳出血',
  [RequestSummaryMedicalHistoryEnum.DEMENTIA]: '認知症',
  [RequestSummaryMedicalHistoryEnum.EPILEPSY]: 'てんかん',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_FAILURE]: '腎不全',
  // 以下は広島では利用しない
  [RequestSummaryMedicalHistoryEnum.DIALYSIS]: '',
  [RequestSummaryMedicalHistoryEnum.MENTAL_ILLNESS]: '',
  [RequestSummaryMedicalHistoryEnum.ARRHYTHMIA]: '',
  [RequestSummaryMedicalHistoryEnum.OTHER_HEART_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.CEREBROVASCULAR]: '',
  [RequestSummaryMedicalHistoryEnum.RESPIRATORY_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.LIVER_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.DIGESTIVE_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.CIRCULATORY_SYSTEM]: '',
  [RequestSummaryMedicalHistoryEnum.NERVOUS_SYSTEM]: '',
  [RequestSummaryMedicalHistoryEnum.ENDOCRINE_SYSTEM]: '',
  [RequestSummaryMedicalHistoryEnum.HEMATOLOGY]: '',
  [RequestSummaryMedicalHistoryEnum.OBSTETRICS_AND_GYNECOLOGY]: '',
  [RequestSummaryMedicalHistoryEnum.INFECTIOUS_DISEASES]: '',
  [RequestSummaryMedicalHistoryEnum.RENAL_UROLOGY]: '',
  [RequestSummaryMedicalHistoryEnum.ALLERGY]: '',
  [RequestSummaryMedicalHistoryEnum.OTHER]: '',
} as const satisfies Record<
  keyof typeof RequestSummaryMedicalHistoryEnum,
  string
>;

/**
 * 傷病者の既往歴の日本語ラベルを取得する関数
 *
 * @param medicalHistory - 傷病者の既往歴
 * @returns 日本語ラベル
 */
const getRequestSummaryMedicalHistoryLabel = (
  medicalHistory:
    | keyof typeof RequestSummaryMedicalHistoryEnum
    | null
    | undefined
): string => {
  if (
    !medicalHistory ||
    !(medicalHistory in REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS)
  ) {
    return '';
  }
  return REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS[medicalHistory];
};

/**
 * 傷病者の日常生活能力の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ADL_LABELS = {
  [RequestSummaryADLEnum.CARE_REQUIRED]: '該当',
  [RequestSummaryADLEnum.INDEPENDENT]: '非該当',
  // 以下は広島では利用しない
  [RequestSummaryADLEnum.BEDRIDDEN]: '',
  [RequestSummaryADLEnum.PARTIAL_ASSISTANCE]: '',
  [RequestSummaryADLEnum.FULL_ASSISTANCE]: '',
  [RequestSummaryADLEnum.UNKNOWN]: '',
} as const satisfies Record<keyof typeof RequestSummaryADLEnum, string>;

/**
 * 傷病者の日常生活能力の日本語ラベルを取得する関数
 *
 * @param adlLevel - 傷病者の日常生活能力
 * @returns 日本語ラベル
 */
const getRequestSummaryADLLabel = (
  adlLevel: keyof typeof RequestSummaryADLEnum | null | undefined
): string => {
  if (!adlLevel || !(adlLevel in REQUEST_SUMMARY_ADL_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ADL_LABELS[adlLevel];
};

/////////////////////////////////////////////
//
// 傷病者 → 身体所見（Findings）
//
/////////////////////////////////////////////
/**
 * 傷病者の失禁状況の日本語ラベルマッピング
 */
const FINDINGS_INCONTINENCE_LABELS = {
  [FindEmsContactIncontinenceEnum.PRESENT_FECAL]: '大',
  [FindEmsContactIncontinenceEnum.PRESENT_URINARY]: '小',
  [FindEmsContactIncontinenceEnum.NONE]: '無',
  // 以下は広島では利用しない
  [FindEmsContactIncontinenceEnum.PRESENT_BOTH]: '',
  [FindEmsContactIncontinenceEnum.UNKNOWN]: '',
} as const satisfies Record<
  keyof typeof FindEmsContactIncontinenceEnum,
  string
>;

/**
 * 傷病者の失禁状況の日本語ラベルを取得する関数
 *
 * @param incontinence - 傷病者の失禁
 * @returns 日本語ラベル
 */
const getFindingsIncontinenceLabel = (
  incontinence: keyof typeof FindEmsContactIncontinenceEnum | null | undefined
): string => {
  if (!incontinence || !(incontinence in FINDINGS_INCONTINENCE_LABELS)) {
    return '';
  }
  return FINDINGS_INCONTINENCE_LABELS[incontinence];
};

const FINDINGS_BLEEDING_VALUE_LABELS = {
  [FindEmsContactBleedingValueEnum.MINIMAL]: '微',
  [FindEmsContactBleedingValueEnum.LOW]: '少',
  [FindEmsContactBleedingValueEnum.MODERATE]: '中',
  [FindEmsContactBleedingValueEnum.HIGH]: '多',
  [FindEmsContactBleedingValueEnum.UNKNOWN]: '不明',
} as const satisfies Record<
  keyof typeof FindEmsContactBleedingValueEnum,
  string
>;

/**
 * 傷病者の出血量の日本語ラベルを取得する関数
 *
 * @param bleedingValue - 傷病者の出血量
 * @returns 日本語ラベル
 */
const getFindingsBleedingValueLabel = (
  bleedingValue: keyof typeof FindEmsContactBleedingValueEnum | null | undefined
): string => {
  if (!bleedingValue || !(bleedingValue in FINDINGS_BLEEDING_VALUE_LABELS)) {
    return '';
  }
  return FINDINGS_BLEEDING_VALUE_LABELS[bleedingValue];
};

/////////////////////////////////////////////
//
// 傷病者 → バイタル（Vital）
//
/////////////////////////////////////////////
/**
 * 測定タイミングの日本語ラベルマッピング
 */
const MEASURE_TIMING_LABELS = {
  [MeasureTimingEnum.SITE]: '現場',
  [MeasureTimingEnum.IN_AMBULANCE]: '車内',
  // 以下は広島では利用しない
  [MeasureTimingEnum.CONTACT]: '',
  [MeasureTimingEnum.TRANSPORTING]: '',
  [MeasureTimingEnum.AMBULANCE_ACCOMMODATION]: '',
  [MeasureTimingEnum.HOSPITAL_ARRIVAL]: '',
  [MeasureTimingEnum.BEFORE_HOSPITAL_ARRIVAL]: '',
  [MeasureTimingEnum.SITE_DEPARTURE]: '',
  [MeasureTimingEnum.OTHER]: '',
} as const satisfies Record<keyof typeof MeasureTimingEnum, string>;

/**
 * 測定タイミングの日本語ラベルを取得する関数
 *
 * @param measureTiming - 測定タイミング
 * @returns 日本語ラベル
 */
const getMeasureTimingLabel = (
  measureTiming: keyof typeof MeasureTimingEnum | null | undefined
): string => {
  if (!measureTiming || !(measureTiming in MEASURE_TIMING_LABELS)) {
    return '';
  }
  return MEASURE_TIMING_LABELS[measureTiming];
};

/**
 * JCSの日本語ラベルマッピング
 */
const JCS_LABELS = {
  [JCSEnum.NUMBER_0]: '0',
  [JCSEnum['1-1']]: '1-1',
  [JCSEnum['1-2']]: '1-2',
  [JCSEnum['1-3']]: '1-3',
  [JCSEnum['2-10']]: '2-10',
  [JCSEnum['2-20']]: '2-20',
  [JCSEnum['2-30']]: '2-30',
  [JCSEnum['3-100']]: '3-100',
  [JCSEnum['3-200']]: '3-200',
  [JCSEnum['3-300']]: '3-300',
  [JCSEnum.OTHER]: 'その他',
} as const satisfies Record<JCSEnum, string>;

const getJCSLabel = (jcs: JCSEnum | null | undefined): string => {
  if (!jcs || !(jcs in JCS_LABELS)) {
    return '';
  }
  return JCS_LABELS[jcs];
};

/**
 * 酸素投与方法(器具)の日本語ラベルマッピング
 */
const VITAL_O2_MEAN_LABELS = {
  [VitalO2MeanEnum.NASAL_CANNULA]: '鼻カニューレ',
  [VitalO2MeanEnum.FACE_MASK]: 'フェイスマスク',
  [VitalO2MeanEnum.RESERVOIR_MASK]: 'リザーバーマスク',
  [VitalO2MeanEnum.VENTILATOR]: '人工呼吸器',
  // 以下は広島では利用しない
  [VitalO2MeanEnum.NONE]: '',
  [VitalO2MeanEnum.VENTURI]: '',
  [VitalO2MeanEnum.BVM]: '',
  [VitalO2MeanEnum.OTHER]: '',
} as const satisfies Record<keyof typeof VitalO2MeanEnum, string>;

/**
 * 酸素投与方法(器具)の日本語ラベルを取得する関数
 *
 * @param o2Mean - 酸素投与方法(器具)
 * @returns 日本語ラベル
 */
const getVitalO2MeanLabel = (
  o2Mean: keyof typeof VitalO2MeanEnum | null | undefined
): string => {
  if (!o2Mean || !(o2Mean in VITAL_O2_MEAN_LABELS)) {
    return '';
  }
  return VITAL_O2_MEAN_LABELS[o2Mean];
};

/**
 * 体温測定部位の日本語ラベルマッピング
 */
const VITAL_BT_PART_LABELS = {
  [VitalBtPartEnum.AXILLA]: '腋窩',
  [VitalBtPartEnum.NON_CONTACT]: '非接触型',
  // 以下は広島では利用しない
  [VitalBtPartEnum.TYMPANIC_MEMBRANE]: '鼓膜',
} as const satisfies Record<keyof typeof VitalBtPartEnum, string>;

/**
 * 体温測定部位の日本語ラベルを取得する関数
 *
 * @param btPart - 体温測定部位
 * @returns 日本語ラベル
 */
const getVitalBtPartLabel = (
  btPart: keyof typeof VitalBtPartEnum | null | undefined
): string => {
  if (!btPart || !(btPart in VITAL_BT_PART_LABELS)) {
    return '';
  }
  return VITAL_BT_PART_LABELS[btPart];
};

/**
 * 心電図波形の日本語ラベルマッピング
 */
const ECG_WAVEFORM_LABELS = {
  [ECGWaveformEnum.NORMAL]: '正常',
  [ECGWaveformEnum.SINUS_RHYTHM]: '洞調律',
  [ECGWaveformEnum.SINUS_BRADYCARDIA]: '洞徐脈',
  [ECGWaveformEnum.SINUS_TACHYCARDIA]: '洞頻脈',
  [ECGWaveformEnum.ASYSTOLE]: '心静止',
  [ECGWaveformEnum.PEA]: 'PEA',
  [ECGWaveformEnum.VENTRICULAR_FIBRILLATION]: '心室細動(VF)',
  [ECGWaveformEnum.VENTRICULAR_TACHYCARDIA]: '心室頻拍(VT)',
  [ECGWaveformEnum.PULSELESS_VT]: '無脈性VT',
  [ECGWaveformEnum.ATRIAL_FIBRILLATION]: '心房細動(Af)',
  [ECGWaveformEnum.PSVT]: '上室性頻拍(PSVT)',
  [ECGWaveformEnum.AV_BLOCK]: '房室ブロック',
  [ECGWaveformEnum.BRADYCARDIA]: '徐脈性不整脈',
  [ECGWaveformEnum.TACHYCARDIA]: '頻脈性不整脈',
  [ECGWaveformEnum.OTHER]: 'その他',
  // 以下は広島では利用しない
  [ECGWaveformEnum.NONE]: '',
  [ECGWaveformEnum.ARRHYTHMIA]: '',
  [ECGWaveformEnum.ATRIAL_FLUTTER]: '',
  [ECGWaveformEnum.FIRST_DEGREE_AV_BLOCK]: '',
  [ECGWaveformEnum.SECOND_DEGREE_AV_BLOCK]: '',
  [ECGWaveformEnum.THIRD_DEGREE_AV_BLOCK]: '',
  [ECGWaveformEnum.BUNDLE_BRANCH_BLOCK]: '',
  [ECGWaveformEnum.ST_ELEVATION]: '',
  [ECGWaveformEnum.ST_DEPRESSION]: '',
  [ECGWaveformEnum.PVC]: '',
  [ECGWaveformEnum.VPC]: '',
} as const satisfies Record<keyof typeof ECGWaveformEnum, string>;

/**
 * 心電図波形の日本語ラベルを取得する関数
 *
 * @param ecgWaveform - 心電図波形
 * @returns 日本語ラベル
 */
const getECGWaveformLabel = (
  ecgWaveform: keyof typeof ECGWaveformEnum | null | undefined
): string => {
  if (!ecgWaveform || !(ecgWaveform in ECG_WAVEFORM_LABELS)) {
    return '';
  }
  return ECG_WAVEFORM_LABELS[ecgWaveform];
};

/**
 * 瞳孔位置の日本語ラベルマッピング
 */
const PUPIL_POSITION_LABELS = {
  [VitalPupilPositionEnum.LEFT]: '左',
  [VitalPupilPositionEnum.MIDDLE]: '中央',
  [VitalPupilPositionEnum.RIGHT]: '右',
} as const satisfies Record<keyof typeof VitalPupilPositionEnum, string>;

/**
 * 瞳孔位置の日本語ラベルを取得する関数
 *
 * @param pupilPosition - 瞳孔位置
 * @returns 日本語ラベル
 */
const getPupilPositionLabel = (
  pupilPosition: keyof typeof VitalPupilPositionEnum | null | undefined
): string => {
  if (!pupilPosition || !(pupilPosition in PUPIL_POSITION_LABELS)) {
    return '';
  }
  return PUPIL_POSITION_LABELS[pupilPosition];
};

/**
 * 瞳孔対光反射の日本語ラベルマッピング
 */
const PUPIL_REACTION_LABELS = {
  [PupilReactionEnum.POSITIVE]: 'あり',
  [PupilReactionEnum.SLUGGISH]: 'あり(鈍)',
  [PupilReactionEnum.NEGATIVE]: 'なし',
  // 以下は広島では利用しない
  [PupilReactionEnum.UNKNOWN]: '',
} as const satisfies Record<keyof typeof PupilReactionEnum, string>;

/**
 * 瞳孔対光反射の日本語ラベルを取得する関数
 *
 * @param pupilReaction - 瞳孔対光反射
 * @returns 日本語ラベル
 */
const getPupilReactionLabel = (
  pupilReaction: keyof typeof PupilReactionEnum | null | undefined
): string => {
  if (!pupilReaction || !(pupilReaction in PUPIL_REACTION_LABELS)) {
    return '';
  }
  return PUPIL_REACTION_LABELS[pupilReaction];
};

/////////////////////////////////////////////
//
// 傷病者 → 特定行為等（SpecialTreatment）
//
/////////////////////////////////////////////
const TREATMENT_CPA_TIMING_LABELS = {
  [TreatmentCPADataTimingEnum.PRESENT]: 'あり',
  [TreatmentCPADataTimingEnum.ABSENT]: 'なし',
  // 以下は広島では利用しない
  [TreatmentCPADataTimingEnum.AFTER_ARRIVAL]: '',
} as const satisfies Record<keyof typeof TreatmentCPADataTimingEnum, string>;

const getTreatmentCPATimingLabel = (
  cpaTiming: keyof typeof TreatmentCPADataTimingEnum | null | undefined
): string => {
  if (!cpaTiming || !(cpaTiming in TREATMENT_CPA_TIMING_LABELS)) {
    return '';
  }
  return TREATMENT_CPA_TIMING_LABELS[cpaTiming];
};

/**
 * 気道確保時使用した器具名の日本語ラベルマッピング
 */
const INTUBATION_DEVICE_LABELS = {
  [IntubationDevice.MANUAL]: '用手',
  [IntubationDevice.LM]: 'ラリンゲルマスク',
  [IntubationDevice.LT]: 'ラリンゲルチューブ',
  [IntubationDevice.TRACHEAL_INTUBATION]: '気管挿管',
  // 以下は広島では利用しない
  [IntubationDevice.VIDEO_LARYNGOSCOPE]: '',
  [IntubationDevice.NASAL_AIRWAY]: '',
  [IntubationDevice.FOREIGN_BODY_REMOVAL]: '',
  [IntubationDevice.OTHER]: '',
} as const satisfies Record<keyof typeof IntubationDevice, string>;

/**
 * 気管挿管装置の日本語ラベルを取得する関数
 *
 * @param intubationDevice - 気管挿管装置
 * @returns 日本語ラベル
 */
const getIntubationDeviceLabel = (
  intubationDevice: keyof typeof IntubationDevice | null | undefined
): string => {
  if (!intubationDevice || !(intubationDevice in INTUBATION_DEVICE_LABELS)) {
    return '';
  }
  return INTUBATION_DEVICE_LABELS[intubationDevice];
};

/**
 * 静脈路確保タイミングの日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路確保のタイミング
 * @returns 日本語ラベル
 */
const TREATMENT_IV_ACCESS_TIMING_LABELS = {
  [TreatmentIvAccessTimingEnum.PRE_CPA]: 'CPA前',
  [TreatmentIvAccessTimingEnum.POST_CPA]: 'CPA後',
  // 以下は広島では利用しない
  [TreatmentIvAccessTimingEnum.GLUCOSE]: '',
  [TreatmentIvAccessTimingEnum.CPA]: '',
  [TreatmentIvAccessTimingEnum.SHOCK]: '',
  [TreatmentIvAccessTimingEnum.CRASH]: '',
} as const satisfies Record<keyof typeof TreatmentIvAccessTimingEnum, string>;

/**
 * 静脈路確保タイミングの日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路適応
 * @returns 日本語ラベル
 */
const getTreatmentIvAccessTimingLabel = (
  ivAccessTiming: keyof typeof TreatmentIvAccessTimingEnum | null | undefined
): string => {
  if (
    !ivAccessTiming ||
    !(ivAccessTiming in TREATMENT_IV_ACCESS_TIMING_LABELS)
  ) {
    return '';
  }
  return TREATMENT_IV_ACCESS_TIMING_LABELS[ivAccessTiming];
};

/////////////////////////////////////////////
//
// 傷病者 → CPAモード（CPAMode）
//
/////////////////////////////////////////////

const TREATMENT_INITIAL_ECG_LABELS = {
  [TreatmentInitialEcgEnum.VF]: 'Vf',
  [TreatmentInitialEcgEnum.VT]: 'VT',
  [TreatmentInitialEcgEnum.PEA]: 'PEA',
  [TreatmentInitialEcgEnum.ASYSTOLE]: 'Asystole',
  // 以下は広島では利用しない
  [TreatmentInitialEcgEnum.NORMAL]: '',
  [TreatmentInitialEcgEnum.OTHER]: '',
} as const satisfies Record<keyof typeof TreatmentInitialEcgEnum, string>;

const getTreatmentInitialEcgLabel = (
  initialEcg: keyof typeof TreatmentInitialEcgEnum | null | undefined
): string => {
  if (!initialEcg || !(initialEcg in TREATMENT_INITIAL_ECG_LABELS)) {
    return '';
  }
  return TREATMENT_INITIAL_ECG_LABELS[initialEcg];
};

export const enumTranslator = (
  data: ReturnType<typeof useIncidentDetail>['incidentDetailData']
) => {
  const { incidentInfo, emsTeamInfo, patientInfo } = data;
  return {
    incidentInfo: {
      ...incidentInfo,
      incidentType: getIncidentTypeLabel(incidentInfo.incidentType),
      enteredUsers:
        incidentInfo.enteredUsers?.map(user => ({
          ...user,
          qualifications:
            user.qualifications?.map(qualification =>
              getEnteredUserQualificationLabel(qualification)
            ) ?? [],
        })) ?? [],
    },
    patientInfo: patientInfo.map(patient => {
      return {
        ...patient,
        baseInfo: {
          ...patient.baseInfo,
          sex: getPatientSexLabel(patient?.baseInfo?.sex),
          occupation: getOccupationLabel(patient?.baseInfo?.occupation),
          relatedPersons: patient?.baseInfo?.relatedPersons ?? [],
          collaboration: patient?.baseInfo?.collaboration ?? [],
          custodyItems: patient?.baseInfo?.custodyItems ?? [],
        },
        requestSummary: {
          ...patient.requestSummary,
          medicationStatusType:
            patient.requestSummary.medicationStatusType?.map(type =>
              getRequestSummaryMedicationStatusTypeLabel(type)
            ) ?? [],
          medicalHistory:
            patient?.requestSummary?.medicalHistory?.map(history =>
              getRequestSummaryMedicalHistoryLabel(history)
            ) ?? [],
          adl: getRequestSummaryADLLabel(patient?.requestSummary?.adl),
          onsetType: getRequestSummaryOnsetTypeLabel(
            patient?.requestSummary?.onsetType
          ),
        },
        vitals: patient.vitals?.map(vital => {
          return {
            ...vital,
            data: {
              ...vital.data,
              jcs: getJCSLabel(vital.data?.jcs),
              o2Means: getVitalO2MeanLabel(vital.data?.o2Means),
              btPart: getVitalBtPartLabel(vital.data?.btPart),
              ecgWaveform: getECGWaveformLabel(vital.data?.ecgWaveform),
              pupilPositionLeft: getPupilPositionLabel(
                vital.data?.pupilPositionLeft
              ),
              pupilPositionRight: getPupilPositionLabel(
                vital.data?.pupilPositionRight
              ),
              pupilReactionLeft: getPupilReactionLabel(
                vital.data?.pupilReactionLeft
              ),
              pupilReactionRight: getPupilReactionLabel(
                vital.data?.pupilReactionRight
              ),
              measureTiming: getMeasureTimingLabel(vital.data?.measureTiming),
            },
          };
        }),
        treatments: patient.treatments?.map(treatment => {
          return {
            ...treatment,
            data: {
              ...treatment.data,
              initialEcg: getTreatmentInitialEcgLabel(
                treatment.data?.initialEcg
              ),
              ivAccessTiming: getTreatmentIvAccessTimingLabel(
                treatment.data?.ivAccessTiming
              ),
              intubationDevice: getIntubationDeviceLabel(
                treatment.data?.intubationDevice
              ),
              cpaData: {
                ...treatment.data?.cpaData,
                cpaTiming: getTreatmentCPATimingLabel(
                  treatment.data?.cpaData?.cpaTiming
                ),
              },
            },
          };
        }),
        findingsEmsContact: {
          ...patient.findingsEmsContact,
          reveals: {
            ...patient.findingsEmsContact?.reveals,
            bleedingValue: getFindingsBleedingValueLabel(
              patient.findingsEmsContact?.reveals?.bleedingValue
            ),
            incontinence: getFindingsIncontinenceLabel(
              patient.findingsEmsContact?.reveals?.incontinence
            ),
          },
        },
        cpaData: {
          ...patient.cpaData,
          initialEcg: getTreatmentInitialEcgLabel(patient.cpaData?.initialEcg),
          contactData: {
            ...patient.cpaData?.contactData,
            pupilsReaction: getPupilReactionLabel(
              patient.cpaData?.contactData?.pupilsReaction
            ),
          },
          accommodationData: {
            ...patient.cpaData?.accommodationData,
            pupilsReaction: getPupilReactionLabel(
              patient.cpaData?.accommodationData?.pupilsReaction
            ),
          },
          hospitalArrivalData: {
            ...patient.cpaData?.hospitalArrivalData,
            pupilsReaction: getPupilReactionLabel(
              patient.cpaData?.hospitalArrivalData?.pupilsReaction
            ),
          },
        },
      };
    }),
    emsTeamInfo: emsTeamInfo,
  };
};
